import { useState } from 'react';
import { VendorSetupPricing } from '@/contracts/vendor-setup';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, GripVertical } from 'lucide-react';

interface Props {
  data: VendorSetupPricing;
  onChange: (data: VendorSetupPricing) => void;
}

export function SetupPricingStep({ data, onChange }: Props) {
  const addPlan = () => {
    onChange({
      ...data,
      plans: [
        ...data.plans,
        {
          name: `Plan ${data.plans.length + 1}`,
          price: null,
          original_price: null,
          features: [],
          setup_fee: undefined,
        },
      ],
    });
  };

  const updatePlan = (index: number, updates: Partial<typeof data.plans[0]>) => {
    const newPlans = [...data.plans];
    newPlans[index] = { ...newPlans[index], ...updates };
    onChange({ ...data, plans: newPlans });
  };

  const removePlan = (index: number) => {
    const newPlans = data.plans.filter((_, i) => i !== index);
    onChange({ ...data, plans: newPlans });
  };

  const addFeature = (planIndex: number) => {
    const newPlans = [...data.plans];
    newPlans[planIndex].features.push('');
    onChange({ ...data, plans: newPlans });
  };

  const updateFeature = (planIndex: number, featureIndex: number, value: string) => {
    const newPlans = [...data.plans];
    newPlans[planIndex].features[featureIndex] = value;
    onChange({ ...data, plans: newPlans });
  };

  const removeFeature = (planIndex: number, featureIndex: number) => {
    const newPlans = [...data.plans];
    newPlans[planIndex].features.splice(featureIndex, 1);
    onChange({ ...data, plans: newPlans });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Pricing Plans</h2>
        <p className="text-muted-foreground">
          Set up your pricing structure. You can offer multiple plans with different features.
        </p>
      </div>

      {/* Base Pricing */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="price">Circle Deal Price *</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <Input
              id="price"
              type="number"
              className="pl-7"
              value={data.price || ''}
              onChange={(e) => onChange({ ...data, price: Number(e.target.value) })}
              placeholder="99"
            />
          </div>
          <p className="text-xs text-muted-foreground">Special price for Circle members</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="msrp">Regular Price (MSRP)</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <Input
              id="msrp"
              type="number"
              className="pl-7"
              value={data.msrp || ''}
              onChange={(e) => onChange({ ...data, msrp: Number(e.target.value) || undefined })}
              placeholder="149"
            />
          </div>
          <p className="text-xs text-muted-foreground">Shown as crossed-out price</p>
        </div>

        <div className="space-y-2">
          <Label>Billing Period</Label>
          <Select
            value={data.price_duration}
            onValueChange={(value) => onChange({ ...data, price_duration: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mo">Per Month</SelectItem>
              <SelectItem value="yr">Per Year</SelectItem>
              <SelectItem value="one-time">One-Time</SelectItem>
              <SelectItem value="lifetime">Lifetime Access</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Pricing Plans */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Pricing Tiers (optional)</Label>
          <Button variant="outline" size="sm" onClick={addPlan}>
            <Plus className="h-4 w-4 mr-1" />
            Add Plan
          </Button>
        </div>

        {data.plans.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed rounded-lg">
            <p className="text-muted-foreground">No pricing tiers yet.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Add tiers if you offer multiple packages (Basic, Pro, Enterprise, etc.)
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {data.plans.map((plan, planIndex) => (
              <Card key={planIndex}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <GripVertical className="h-5 w-5 text-muted-foreground" />
                    <Input
                      value={plan.name}
                      onChange={(e) => updatePlan(planIndex, { name: e.target.value })}
                      className="font-semibold text-lg"
                      placeholder="Plan name"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removePlan(planIndex)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Price</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          className="pl-7"
                          value={plan.price ?? ''}
                          onChange={(e) => updatePlan(planIndex, { 
                            price: e.target.value ? Number(e.target.value) : null 
                          })}
                          placeholder="99"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Original Price</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          className="pl-7"
                          value={plan.original_price ?? ''}
                          onChange={(e) => updatePlan(planIndex, { 
                            original_price: e.target.value ? Number(e.target.value) : null 
                          })}
                          placeholder="149"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Setup Fee</Label>
                      <Input
                        value={plan.setup_fee || ''}
                        onChange={(e) => updatePlan(planIndex, { setup_fee: e.target.value || undefined })}
                        placeholder="$0 or Free"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Features Included</Label>
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex gap-2">
                        <Input
                          value={feature}
                          onChange={(e) => updateFeature(planIndex, featureIndex, e.target.value)}
                          placeholder="e.g., Unlimited contacts"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFeature(planIndex, featureIndex)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addFeature(planIndex)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Feature
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
